import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Save, Download, Database, LogOut, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatNumber, formatEuro } from "@/lib/utils";

export default function EinstellungenPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPw, setChangingPw] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setSettings(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put("/settings", {
        daily_goal: parseInt(settings.daily_goal),
        weekly_goal: parseInt(settings.weekly_goal),
        yearly_goal: parseInt(settings.yearly_goal),
        point_value_euro: parseFloat(settings.point_value_euro),
        start_value: parseInt(settings.start_value),
        start_date: settings.start_date,
        start_projekte: parseInt(settings.start_projekte) || 0,
        start_auftragswert: parseFloat(settings.start_auftragswert) || 0,
        start_date_business: settings.start_date_business || settings.start_date,
        yearly_goal_orders: parseInt(settings.yearly_goal_orders) || 0,
      });
      setSettings(res.data);
      toast.success("Einstellungen gespeichert");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/entries/export/csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "punktesystem_export.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export heruntergeladen");
    } catch {
      toast.error("Export fehlgeschlagen");
    }
  };

  const handleFullBackup = async () => {
    try {
      const res = await api.get("/export/full-backup", { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename=([^;]+)/);
      const fname = m ? m[1].trim() : `gimini_backup_${new Date().toISOString().slice(0, 10)}.json`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Vollständige JSON-Sicherung heruntergeladen");
    } catch {
      toast.error("Sicherung fehlgeschlagen");
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await api.post("/seed");
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setSeeding(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 4) {
      toast.error("Mindestens 4 Zeichen");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwoerter stimmen nicht ueberein");
      return;
    }
    setChangingPw(true);
    try {
      const res = await api.put("/auth/password", { password: newPassword });
      localStorage.setItem("token", res.data.token);
      setNewPassword("");
      setConfirmPassword("");
      toast.success("Passwort geaendert");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setChangingPw(false);
    }
  };

  const set = (key, val) => setSettings((p) => ({ ...p, [key]: val }));

  if (loading) {
    return (
      <div className="p-4 md:p-8 space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-20 bg-zinc-900/50 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!settings) return <div className="p-8 text-zinc-500">Fehler beim Laden.</div>;

  return (
    <div className="p-4 md:p-8 space-y-6 max-w-2xl mx-auto" data-testid="einstellungen-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">
          Einstellungen
        </h1>
        <p className="text-zinc-600 text-sm mt-0.5">Ziele und Konfiguration</p>
      </div>

      {/* Goals */}
      <Card className="bg-card border-border">
        <CardContent className="p-5 space-y-4">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500">Ziele</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-zinc-400">Tagesziel (Punkte)</Label>
              <Input
                type="number"
                value={settings.daily_goal}
                onChange={(e) => set("daily_goal", e.target.value)}
                className="bg-zinc-950/50 border-zinc-800 h-10 font-mono mt-1"
                data-testid="setting-daily-goal"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Wochenziel (Punkte)</Label>
              <Input
                type="number"
                value={settings.weekly_goal}
                onChange={(e) => set("weekly_goal", e.target.value)}
                className="bg-zinc-950/50 border-zinc-800 h-10 font-mono mt-1"
                data-testid="setting-weekly-goal"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Jahresziel (Punkte)</Label>
              <Input
                type="number"
                value={settings.yearly_goal}
                onChange={(e) => set("yearly_goal", e.target.value)}
                className="bg-zinc-950/50 border-zinc-800 h-10 font-mono mt-1"
                data-testid="setting-yearly-goal"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Values */}
      <Card className="bg-card border-border">
        <CardContent className="p-5 space-y-4">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500">Werte</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-zinc-400">Punktwert (Euro)</Label>
              <Input
                type="number"
                step="0.01"
                value={settings.point_value_euro}
                onChange={(e) => set("point_value_euro", e.target.value)}
                className="bg-zinc-950/50 border-zinc-800 h-10 font-mono mt-1"
                data-testid="setting-point-value"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Startwert (Punkte)</Label>
              <Input
                type="number"
                value={settings.start_value}
                onChange={(e) => set("start_value", e.target.value)}
                className="bg-zinc-950/50 border-zinc-800 h-10 font-mono mt-1"
                data-testid="setting-start-value"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Startdatum</Label>
              <Input
                type="date"
                value={settings.start_date}
                onChange={(e) => set("start_date", e.target.value)}
                className="bg-zinc-950/50 border-zinc-800 h-10 font-mono mt-1"
                data-testid="setting-start-date"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Startwerte */}
      <Card className="bg-card border-l-2 border-l-emerald-600 border-t-0 border-r border-b border-border">
        <CardContent className="p-5 space-y-4">
          <p className="text-[11px] uppercase tracking-wider text-emerald-500/70">Startwerte Auftraege</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-zinc-400">Startzahl Projekte</Label>
              <Input
                type="number"
                min="0"
                value={settings.start_projekte ?? 0}
                onChange={(e) => set("start_projekte", e.target.value)}
                className="bg-zinc-950/50 border-zinc-800 h-10 font-mono mt-1"
                data-testid="setting-start-projekte"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Startwert Auftragswert (EUR)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={settings.start_auftragswert ?? 0}
                onChange={(e) => set("start_auftragswert", e.target.value)}
                className="bg-zinc-950/50 border-zinc-800 h-10 font-mono mt-1"
                data-testid="setting-start-auftragswert"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Startdatum Business</Label>
              <Input
                type="date"
                value={settings.start_date_business || settings.start_date}
                onChange={(e) => set("start_date_business", e.target.value)}
                className="bg-zinc-950/50 border-zinc-800 h-10 font-mono mt-1"
                data-testid="setting-start-date-business"
              />
            </div>
          </div>
          <p className="text-[9px] text-zinc-700">Bereits gewonnene Projekte und Auftragswerte vor dem Startdatum manuell eintragen.</p>
          <Separator className="bg-zinc-900 my-2" />
          <div>
            <Label className="text-xs text-zinc-400">Jahresziel Auftraege</Label>
            <Input
              type="number"
              min="0"
              value={settings.yearly_goal_orders ?? 0}
              onChange={(e) => set("yearly_goal_orders", e.target.value)}
              className="bg-zinc-950/50 border-zinc-800 h-10 font-mono mt-1"
              data-testid="setting-yearly-goal-orders"
            />
            <p className="text-[9px] text-zinc-700 mt-1">Fuer Angebots- und Cold-Call-Prognose auf der Vertriebsseite.</p>
          </div>
        </CardContent>
      </Card>

      <Button
        onClick={handleSave}
        disabled={saving}
        className="w-full h-11 bg-white text-black hover:bg-zinc-200 font-medium"
        data-testid="save-settings-btn"
      >
        <Save className="mr-2 h-4 w-4" />
        {saving ? "Speichern..." : "Einstellungen speichern"}
      </Button>

      <Separator className="bg-zinc-900" />

      {/* Password Change */}
      <Card className="bg-card border-border">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-zinc-500" />
            <p className="text-[11px] uppercase tracking-wider text-zinc-500">Passwort aendern</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-zinc-400">Neues Passwort</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Min. 4 Zeichen"
                className="bg-zinc-950/50 border-zinc-800 h-10 mt-1"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Passwort bestaetigen</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Wiederholen"
                className="bg-zinc-950/50 border-zinc-800 h-10 mt-1"
                data-testid="confirm-new-password-input"
              />
            </div>
          </div>
          <Button
            onClick={handleChangePassword}
            disabled={changingPw || !newPassword}
            variant="outline"
            className="w-full h-10 border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            data-testid="change-password-btn"
          >
            <Lock className="mr-2 h-4 w-4 text-zinc-500" />
            {changingPw ? "Aendern..." : "Passwort aendern"}
          </Button>
        </CardContent>
      </Card>

      <Separator className="bg-zinc-900" />

      {/* Actions */}
      <div className="space-y-3">
        <p className="text-[11px] uppercase tracking-wider text-zinc-500">Aktionen</p>

        <Button
          variant="outline"
          onClick={handleFullBackup}
          className="w-full justify-start h-11 border-emerald-800/50 text-emerald-300 hover:bg-emerald-950/20"
          data-testid="full-backup-btn"
        >
          <Download className="mr-3 h-4 w-4 text-emerald-400" />
          Vollständige JSON-Sicherung
        </Button>

        <Button
          variant="outline"
          onClick={handleExport}
          className="w-full justify-start h-11 border-zinc-800 text-zinc-300 hover:bg-zinc-900"
          data-testid="export-csv-btn"
        >
          <Download className="mr-3 h-4 w-4 text-zinc-500" />
          CSV Export (Tagesdaten)
        </Button>

        <Button
          variant="outline"
          onClick={handleSeed}
          disabled={seeding}
          className="w-full justify-start h-11 border-zinc-800 text-zinc-300 hover:bg-zinc-900"
          data-testid="seed-data-btn"
        >
          <Database className="mr-3 h-4 w-4 text-zinc-500" />
          {seeding ? "Laden..." : "Demo-Daten laden"}
        </Button>

        <Button
          variant="outline"
          onClick={handleLogout}
          className="w-full justify-start h-11 border-zinc-800 text-red-400 hover:bg-red-500/10 hover:text-red-400"
          data-testid="logout-btn"
        >
          <LogOut className="mr-3 h-4 w-4" />
          Abmelden
        </Button>
      </div>
    </div>
  );
}
